import { useEffect, useRef, useState } from "react";

/**
 * Custom hook to detect user idle time
 * @param {number} idleTimeMs - Idle timeout in milliseconds (default: 900000 = 15 minutes)
 * @param {function} onIdleDetected - Callback when user has been idle for the specified time
 * @returns {object} - { isIdle, resetIdleTimer }
 */
export const useIdleTimer = (idleTimeMs = 900000, onIdleDetected = null) => {
  const [isIdle, setIsIdle] = useState(false);
  const idleTimerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const isIdleRef = useRef(false);

  // Activity events to track
  const activityEvents = ["mousemove", "keydown", "click", "scroll", "touchstart"];

  /**
   * Resets the idle timer and clears the idle state
   */
  const resetIdleTimer = () => {
    // Clear existing timers
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }
    // Reset state
    lastActivityRef.current = Date.now();
    setIsIdle(false);
    isIdleRef.current = false;

    // Set up new idle timer
    idleTimerRef.current = setTimeout(() => {
      setIsIdle(true);
      isIdleRef.current = true;
      if (onIdleDetected) {
        onIdleDetected();
      }
    }, idleTimeMs);
  };

  /**
   * Handle user activity
   */
  const handleActivity = () => {
    if (!isIdleRef.current) {
      // User is not idle yet, reset the timer
      resetIdleTimer();
    } else {
      // User was idle, now they're active again
      resetIdleTimer();
    }
  };

  // Set up event listeners on mount
  useEffect(() => {
    resetIdleTimer();

    // Add event listeners
    activityEvents.forEach((event) => {
      document.addEventListener(event, handleActivity);
    });

    // Cleanup on unmount
    return () => {
      // Clear timers
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }

      // Remove event listeners
      activityEvents.forEach((event) => {
        document.removeEventListener(event, handleActivity);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idleTimeMs]);

  return {
    isIdle,
    resetIdleTimer,
  };
};

export default useIdleTimer;
